import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { useDataLayerValue } from "../../DataLayer/DataLayer";
import { uploadFile } from "../../utils/ApiCalls/Document";
import {
  createLoginPage,
  updateLoginPageData,
  getLoginPageSettings,
  saveLoginPageSettings,
} from "../../utils/ApiCalls/LoginPage";
import { constants } from "../../constants/constants";
import UploadFileImageBanner2 from "../Banner/uploadFileImageBanner copy";
import AltTextInput, { pickAltText } from "../AltTextInput/AltTextInput";

const LOGIN_SLIDER_ALT_FIELDS = [
  "image_alt",
  "simplified_chinese_image_alt",
  "traditional_chinese_image_alt",
  "bahasa_image_alt",
  "image_mobile_alt",
  "simplified_chinese_image_mobile_alt",
  "traditional_chinese_image_mobile_alt",
  "bahasa_image_mobile_alt",
];

const LoginPageInput = ({ slider }) => {
  const navigate = useNavigate();
  const [, dispatch] = useDataLayerValue();

  // --- Common SEO fields (shared across all sliders) ---
  const [englishPageTitle, setEnglishPageTitle] = useState("");
  const [simplifiedChinesePageTitle, setSimplifiedChinesePageTitle] = useState("");
  const [traditionalChinesePageTitle, setTraditionalChinesePageTitle] = useState("");
  const [bahasaPageTitle, setBahasaPageTitle] = useState("");
  const [englishMetaDescription, setEnglishMetaDescription] = useState("");
  const [simplifiedChineseMetaDescription, setSimplifiedChineseMetaDescription] = useState("");
  const [traditionalChineseMetaDescription, setTraditionalChineseMetaDescription] = useState("");
  const [bahasaMetaDescription, setBahasaMetaDescription] = useState("");

  useEffect(() => {
    getLoginPageSettings().then((data) => {
      if (data && !data.error) {
        setEnglishPageTitle(data.english_page_title || "");
        setSimplifiedChinesePageTitle(data.simplified_chinese_page_title || "");
        setTraditionalChinesePageTitle(data.traditional_chinese_page_title || "");
        setBahasaPageTitle(data.bahasa_page_title || "");
        setEnglishMetaDescription(data.english_meta_description || "");
        setSimplifiedChineseMetaDescription(data.simplified_chinese_meta_description || "");
        setTraditionalChineseMetaDescription(data.traditional_chinese_meta_description || "");
        setBahasaMetaDescription(data.bahasa_meta_description || "");
      }
    });
  }, []);

  const [englishImage, setEnglishImage] = useState(null);
  const [simplifiedChineseImage, setSimplifiedChineseImage] = useState(null);
  const [traditionalChineseImage, setTraditionalChineseImage] = useState(null);
  const [bahasaImage, setBahasaImage] = useState(null);

  const [englishMobileImage, setEnglishMobileImage] = useState(null);
  const [simplifiedChineseMobileImage, setSimplifiedChineseMobileImage] =
    useState(null);
  const [traditionalChineseMobileImage, setTraditionalChineseMobileImage] =
    useState(null);
  const [bahasaMobileImage, setBahasaMobileImage] = useState(null);

  const [englishImageUrl] = useState(slider?.image || "");
  const [simplifiedChineseImageUrl] = useState(
    slider?.simplified_chinese_image || ""
  );
  const [traditionalChineseImageUrl] = useState(
    slider?.traditional_chinese_image || ""
  );
  const [bahasaImageUrl] = useState(slider?.bahasa_image || "");

  const [englishMobileImageUrl] = useState(
    slider?.image_mobile || ""
  );
  const [simplifiedChineseMobileImageUrl] = useState(
    slider?.simplified_chinese_image_mobile || ""
  );
  const [traditionalChineseMobileImageUrl] = useState(
    slider?.traditional_chinese_image_mobile || ""
  );
  const [bahasaMobileImageUrl] = useState(slider?.bahasa_image_mobile || "");

  const [altText, setAltText] = useState({});
  useEffect(() => {
    setAltText(pickAltText(slider, LOGIN_SLIDER_ALT_FIELDS));
  }, [slider]);

  const handleUpload = async (file, existingImage) => {
    if (!file) return existingImage;

    if (typeof file === "string") {
      return file.replace(constants.MEDIAURL + "/", "");
    }

    const uploaded = await uploadFile(file, undefined, undefined, typeof existingImage === 'string' ? existingImage.replace(constants.MEDIAURL + "/", "").replace(constants.MEDIAURL, "") : null);
    if (uploaded?.error) throw new Error(uploaded.error);

    return uploaded.url;
  };

  const getValidationError = () => {
    const seoFields = [
      [englishPageTitle, "English Page Title"],
      [simplifiedChinesePageTitle, "Simplified Chinese Page Title"],
      [traditionalChinesePageTitle, "Traditional Chinese Page Title"],
      [bahasaPageTitle, "Bahasa Page Title"],
      [englishMetaDescription, "English Meta Description"],
      [simplifiedChineseMetaDescription, "Simplified Chinese Meta Description"],
      [traditionalChineseMetaDescription, "Traditional Chinese Meta Description"],
      [bahasaMetaDescription, "Bahasa Meta Description"],
    ];
    for (const [value, label] of seoFields) {
      if (!value.trim()) return `${label} is required`;
    }
    if (!englishImage && !englishImageUrl) return "English desktop image is required";
    if (!englishMobileImage && !englishMobileImageUrl) return "English mobile image is required";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const error = getValidationError();
    if (error) {
      Swal.fire("Error", error, "error");
      return;
    }

    dispatch({ type: "SET_LOADING", loading: true });

    try {
      const image = await handleUpload(
        englishImage,
        englishImageUrl
      );
      const simplified_chinese_image = await handleUpload(
        simplifiedChineseImage,
        simplifiedChineseImageUrl
      );
      const traditional_chinese_image = await handleUpload(
        traditionalChineseImage,
        traditionalChineseImageUrl
      );
      const bahasa_image = await handleUpload(
        bahasaImage,
        bahasaImageUrl
      );

      const image_mobile = await handleUpload(
        englishMobileImage,
        englishMobileImageUrl
      );
      const simplified_chinese_image_mobile = await handleUpload(
        simplifiedChineseMobileImage,
        simplifiedChineseMobileImageUrl
      );
      const traditional_chinese_image_mobile = await handleUpload(
        traditionalChineseMobileImage,
        traditionalChineseMobileImageUrl
      );
      const bahasa_image_mobile = await handleUpload(
        bahasaMobileImage,
        bahasaMobileImageUrl
      );

      const seoData = {
        english_page_title: englishPageTitle,
        simplified_chinese_page_title: simplifiedChinesePageTitle,
        traditional_chinese_page_title: traditionalChinesePageTitle,
        bahasa_page_title: bahasaPageTitle,
        english_meta_description: englishMetaDescription,
        simplified_chinese_meta_description: simplifiedChineseMetaDescription,
        traditional_chinese_meta_description: traditionalChineseMetaDescription,
        bahasa_meta_description: bahasaMetaDescription,
      };

      const payload = {
        image,
        simplified_chinese_image,
        traditional_chinese_image,
        bahasa_image,

        image_mobile,
        simplified_chinese_image_mobile,
        traditional_chinese_image_mobile,
        bahasa_image_mobile,

        ...altText,

        is_active: 1,
      };

      await saveLoginPageSettings(seoData);
      if (slider?.id) {
        await updateLoginPageData(slider.id, payload);
      } else {
        await createLoginPage({ ...payload, ...seoData });
      }

      Swal.fire(
        "Success",
        slider?.id
          ? "Login slider images updated"
          : "Login slider images added",
        "success"
      );

      navigate("/website/webpages/login-page");
    } catch (error) {
      Swal.fire(
        "Error",
        error.message || "Something went wrong",
        "error"
      );
    } finally {
      dispatch({ type: "SET_LOADING", loading: false });
    }
  };

  return (
    <section className="content">
      <div className="container-fluid">
        <div className="card">
          <div className="card-header">
            <h5 className="card-title">Page SEO (Common for all sliders)</h5>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-lg-3">
                <div className="form-group">
                  <label htmlFor="englishPageTitle">English Page Title <span className="text-danger">*</span></label>
                  <input
                    id="englishPageTitle"
                    type="text"
                    className="form-control"
                    value={englishPageTitle}
                    onChange={(e) => setEnglishPageTitle(e.target.value)}
                    placeholder="English Page Title"
                  />
                </div>
              </div>
              <div className="col-lg-3">
                <div className="form-group">
                  <label htmlFor="simplifiedChinesePageTitle">Simplified Chinese Page Title <span className="text-danger">*</span></label>
                  <input
                    id="simplifiedChinesePageTitle"
                    type="text"
                    className="form-control"
                    value={simplifiedChinesePageTitle}
                    onChange={(e) => setSimplifiedChinesePageTitle(e.target.value)}
                    placeholder="Simplified Chinese Page Title"
                  />
                </div>
              </div>
              <div className="col-lg-3">
                <div className="form-group">
                  <label htmlFor="traditionalChinesePageTitle">Traditional Chinese Page Title <span className="text-danger">*</span></label>
                  <input
                    id="traditionalChinesePageTitle"
                    type="text"
                    className="form-control"
                    value={traditionalChinesePageTitle}
                    onChange={(e) => setTraditionalChinesePageTitle(e.target.value)}
                    placeholder="Traditional Chinese Page Title"
                  />
                </div>
              </div>
              <div className="col-lg-3">
                <div className="form-group">
                  <label htmlFor="bahasaPageTitle">Bahasa Page Title <span className="text-danger">*</span></label>
                  <input
                    id="bahasaPageTitle"
                    type="text"
                    className="form-control"
                    value={bahasaPageTitle}
                    onChange={(e) => setBahasaPageTitle(e.target.value)}
                    placeholder="Bahasa Page Title"
                  />
                </div>
              </div>
              <div className="col-lg-3">
                <div className="form-group">
                  <label htmlFor="englishMetaDescription">English Meta Description <span className="text-danger">*</span></label>
                  <textarea
                    id="englishMetaDescription"
                    className="form-control"
                    rows={3}
                    value={englishMetaDescription}
                    onChange={(e) => setEnglishMetaDescription(e.target.value)}
                    placeholder="English Meta Description"
                  />
                </div>
              </div>
              <div className="col-lg-3">
                <div className="form-group">
                  <label htmlFor="simplifiedChineseMetaDescription">Simplified Chinese Meta Description <span className="text-danger">*</span></label>
                  <textarea
                    id="simplifiedChineseMetaDescription"
                    className="form-control"
                    rows={3}
                    value={simplifiedChineseMetaDescription}
                    onChange={(e) => setSimplifiedChineseMetaDescription(e.target.value)}
                    placeholder="Simplified Chinese Meta Description"
                  />
                </div>
              </div>
              <div className="col-lg-3">
                <div className="form-group">
                  <label htmlFor="traditionalChineseMetaDescription">Traditional Chinese Meta Description <span className="text-danger">*</span></label>
                  <textarea
                    id="traditionalChineseMetaDescription"
                    className="form-control"
                    rows={3}
                    value={traditionalChineseMetaDescription}
                    onChange={(e) => setTraditionalChineseMetaDescription(e.target.value)}
                    placeholder="Traditional Chinese Meta Description"
                  />
                </div>
              </div>
              <div className="col-lg-3">
                <div className="form-group">
                  <label htmlFor="bahasaMetaDescription">Bahasa Meta Description <span className="text-danger">*</span></label>
                  <textarea
                    id="bahasaMetaDescription"
                    className="form-control"
                    rows={3}
                    value={bahasaMetaDescription}
                    onChange={(e) => setBahasaMetaDescription(e.target.value)}
                    placeholder="Bahasa Meta Description"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <form className="input_form" onSubmit={handleSubmit}>
            <div className="card-body">
              <div className="row">
                <UploadFileImageBanner2
                  className="col-lg-3"
                  mediaType="image"
                  width={1300}
                  height={1352}
                  label="English Login Page Image"
                  placeholder="Upload English Login Page Image"
                  image={
                    slider?.image
                      ? `${constants.MEDIAURL}/${slider.image}`
                      : null
                  }
                  selectedFile={englishImage}
                  setSelectedFile={setEnglishImage}
                />

                <UploadFileImageBanner2
                  className="col-lg-3"
                  mediaType="image"
                  width={1300}
                  height={1352}
                  label="Simplified Chinese Image"
                  placeholder="Upload Simplified Chinese Image"
                  image={
                    slider?.simplified_chinese_image
                      ? `${constants.MEDIAURL}/${slider.simplified_chinese_image}`
                      : null
                  }
                  selectedFile={simplifiedChineseImage}
                  setSelectedFile={setSimplifiedChineseImage}
                />

                <UploadFileImageBanner2
                  className="col-lg-3"
                  mediaType="image"
                  width={1300}
                  height={1352}
                  label="Traditional Chinese Image"
                  placeholder="Upload Traditional Chinese Image"
                  image={
                    slider?.traditional_chinese_image
                      ? `${constants.MEDIAURL}/${slider.traditional_chinese_image}`
                      : null
                  }
                  selectedFile={traditionalChineseImage}
                  setSelectedFile={setTraditionalChineseImage}
                />

                <UploadFileImageBanner2
                  className="col-lg-3"
                  mediaType="image"
                  width={1300}
                  height={1352}
                  label="Bahasa Image"
                  placeholder="Upload Bahasa Image"
                  image={
                    slider?.bahasa_image
                      ? `${constants.MEDIAURL}/${slider.bahasa_image}`
                      : null
                  }
                  selectedFile={bahasaImage}
                  setSelectedFile={setBahasaImage}
                />
                <AltTextInput className="col-lg-3" label="English Login Page Image Alt Text" field="image_alt" values={altText} setValues={setAltText} />
                <AltTextInput className="col-lg-3" label="Simplified Chinese Image Alt Text" field="simplified_chinese_image_alt" values={altText} setValues={setAltText} />
                <AltTextInput className="col-lg-3" label="Traditional Chinese Image Alt Text" field="traditional_chinese_image_alt" values={altText} setValues={setAltText} />
                <AltTextInput className="col-lg-3" label="Bahasa Image Alt Text" field="bahasa_image_alt" values={altText} setValues={setAltText} />

                <UploadFileImageBanner2
                  className="col-lg-3"
                  mediaType="image"
                  width={860}
                  height={1210}
                  label="English Login Page Mobile Image "
                  placeholder="Upload English Login Page Mobile Image "
                  image={
                    slider?.image_mobile
                      ? `${constants.MEDIAURL}/${slider.image_mobile}`
                      : null
                  }
                  selectedFile={englishMobileImage}
                  setSelectedFile={setEnglishMobileImage}
                />

                <UploadFileImageBanner2
                  className="col-lg-3"
                  mediaType="image"
                  width={860}
                  height={1210}
                  label="Simplified Chinese Mobile Image"
                  placeholder="Upload Simplified Chinese Mobile Image"
                  image={
                    slider?.simplified_chinese_image_mobile
                      ? `${constants.MEDIAURL}/${slider.simplified_chinese_image_mobile}`
                      : null
                  }
                  selectedFile={simplifiedChineseMobileImage}
                  setSelectedFile={setSimplifiedChineseMobileImage}
                />

                <UploadFileImageBanner2
                  className="col-lg-3"
                  mediaType="image"
                  width={860}
                  height={1210}
                  label="Traditional Chinese Mobile Image"
                  placeholder="Upload Traditional Chinese Mobile Image"
                  image={
                    slider?.traditional_chinese_image_mobile
                      ? `${constants.MEDIAURL}/${slider.traditional_chinese_image_mobile}`
                      : null
                  }
                  selectedFile={traditionalChineseMobileImage}
                  setSelectedFile={setTraditionalChineseMobileImage}
                />

                <UploadFileImageBanner2
                  className="col-lg-3"
                  mediaType="image"
                  width={860}
                  height={1210}
                  label="Bahasa Mobile Image"
                  placeholder="Upload Bahasa Mobile Image"
                  image={
                    slider?.bahasa_image_mobile
                      ? `${constants.MEDIAURL}/${slider.bahasa_image_mobile}`
                      : null
                  }
                  selectedFile={bahasaMobileImage}
                  setSelectedFile={setBahasaMobileImage}
                />
                <AltTextInput className="col-lg-3" label="English Mobile Image Alt Text" field="image_mobile_alt" values={altText} setValues={setAltText} />
                <AltTextInput className="col-lg-3" label="Simplified Chinese Mobile Image Alt Text" field="simplified_chinese_image_mobile_alt" values={altText} setValues={setAltText} />
                <AltTextInput className="col-lg-3" label="Traditional Chinese Mobile Image Alt Text" field="traditional_chinese_image_mobile_alt" values={altText} setValues={setAltText} />
                <AltTextInput className="col-lg-3" label="Bahasa Mobile Image Alt Text" field="bahasa_image_mobile_alt" values={altText} setValues={setAltText} />
              </div>
            </div>

            <div className="card-footer text-right">
              <button className="btn main_submit">
                {slider?.id ? "Update" : "Add"} Slider Images
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

LoginPageInput.propTypes = {
  slider: PropTypes.shape({
    id: PropTypes.number,
    image: PropTypes.string,
    simplified_chinese_image: PropTypes.string,
    traditional_chinese_image: PropTypes.string,
    bahasa_image: PropTypes.string,
    image_mobile: PropTypes.string,
    simplified_chinese_image_mobile: PropTypes.string,
    traditional_chinese_image_mobile: PropTypes.string,
    bahasa_image_mobile: PropTypes.string,
  }),
};

export default LoginPageInput;
